import React, { useCallback, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, TableProperties, Layers, Settings, Bell, Search, UserCircle, ChevronLeft, ChevronRight, Menu, Wallet, Receipt, User as UserIcon } from 'lucide-react';
import { cn } from './lib/utils';
import { Overview } from './components/Overview';
import { RateTables } from './components/RateTables';
import { CDSTiers } from './components/CDSTiers';
import { Draws } from './components/Draws';
import { FeesRecoupments } from './components/FeesRecoupments';
import { LoanOfficerView } from './components/LoanOfficerView';
import { Input } from './components/ui/Input';
import { Button } from './components/ui/Button';
import { useScreenInit } from './useScreenInit';
type View = 'overview' | 'rate-tables' | 'cds-tiers' | 'draws' | 'fees' | 'lo-view';
export function App() {
  const screenInit = useScreenInit();
  const [currentView, setCurrentView] = useState<View>(screenInit?.currentView as View ?? 'overview');
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [focusUserId, setFocusUserId] = useState<string | null>(null);
  const navigateToUser = useCallback((user: {
    id: string;
    model: 'BPS' | 'CDS';
  }) => {
    setFocusUserId(user.id);
    setCurrentView(user.model === 'CDS' ? 'cds-tiers' : 'rate-tables');
  }, []);
  const navigation = [{
    name: 'Overview',
    view: 'overview' as View,
    icon: LayoutDashboard
  }, {
    name: 'BPS Rate Tables',
    view: 'rate-tables' as View,
    icon: TableProperties
  }, {
    name: 'CDS Tiers',
    view: 'cds-tiers' as View,
    icon: Layers
  }, {
    name: 'Draws',
    view: 'draws' as View,
    icon: Wallet
  }, {
    name: 'Fees & Recoupments',
    view: 'fees' as View,
    icon: Receipt
  }, {
    name: 'LO View',
    view: 'lo-view' as View,
    icon: UserIcon
  }];
  return <BrowserRouter data-id="element-0">
      <div className="flex h-screen w-full bg-slate-50 text-slate-900 font-sans overflow-hidden" data-id="element-1">
        {/* Dark Collapsible Icon Rail */}
        <motion.div initial={false} animate={{
        width: isNavExpanded ? 240 : 72
      }} className="flex flex-col bg-slate-950 text-slate-400 border-r border-slate-900 shrink-0 relative z-20 shadow-xl" data-id="element-2">
          <div className="flex h-14 items-center px-4 border-b border-slate-800/50 shrink-0" data-id="element-3">
            <div className="flex items-center gap-3 font-semibold text-slate-100 overflow-hidden whitespace-nowrap" data-id="element-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-600 text-white shadow-sm" data-id="element-5">
                <Layers className="h-5 w-5" data-id="element-6" />
              </div>
              <AnimatePresence mode="wait" data-id="element-7">
                {isNavExpanded && <motion.span initial={{
                opacity: 0,
                width: 0
              }} animate={{
                opacity: 1,
                width: 'auto'
              }} exit={{
                opacity: 0,
                width: 0
              }} className="text-lg tracking-tight" data-id="element-8">
                    Commission Manager
                  </motion.span>}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 flex flex-col gap-2 px-3" data-id="element-9">
            {navigation.map(item => {
            const Icon = item.icon;
            const isActive = currentView === item.view;
            return <button key={item.name} onClick={() => setCurrentView(item.view)} className={cn('group relative flex items-center rounded-lg p-2.5 transition-all duration-200 w-full text-left', isActive ? 'bg-slate-800 text-white shadow-sm' : 'hover:bg-slate-800/50 hover:text-slate-200')} data-id="element-10">
                  {isActive && <motion.div layoutId="active-nav-indicator" className="absolute left-0 top-2 bottom-2 w-1 bg-blue-500 rounded-r-full" data-id="element-11" />}
                  <Icon className={cn('h-5 w-5 shrink-0', isActive ? 'text-blue-400' : '')} data-id="element-12" />

                  <AnimatePresence mode="wait" data-id="element-13">
                    {isNavExpanded && <motion.span initial={{
                  opacity: 0,
                  width: 0
                }} animate={{
                  opacity: 1,
                  width: 'auto'
                }} exit={{
                  opacity: 0,
                  width: 0
                }} className="ml-3 text-sm font-medium whitespace-nowrap overflow-hidden" data-id="element-14">
                        {item.name}
                      </motion.span>}
                  </AnimatePresence>

                  {/* Tooltip for collapsed state */}
                  {!isNavExpanded && <div className="absolute left-full ml-4 px-2.5 py-1.5 bg-slate-800 text-slate-100 text-xs font-medium rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 shadow-lg transition-opacity" data-id="element-15">
                      {item.name}
                    </div>}
                </button>;
          })}
          </div>

          <div className="mt-auto p-3 border-t border-slate-800/50 flex flex-col gap-2" data-id="element-16">
            <button className="group relative flex items-center rounded-lg p-2.5 text-slate-400 transition-all hover:bg-slate-800/50 hover:text-slate-200 w-full text-left" data-id="element-17">
              <Settings className="h-5 w-5 shrink-0" data-id="element-18" />
              <AnimatePresence mode="wait" data-id="element-19">
                {isNavExpanded && <motion.span initial={{
                opacity: 0,
                width: 0
              }} animate={{
                opacity: 1,
                width: 'auto'
              }} exit={{
                opacity: 0,
                width: 0
              }} className="ml-3 text-sm font-medium whitespace-nowrap overflow-hidden" data-id="element-20">
                    Settings
                  </motion.span>}
              </AnimatePresence>
              {!isNavExpanded && <div className="absolute left-full ml-4 px-2.5 py-1.5 bg-slate-800 text-slate-100 text-xs font-medium rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 shadow-lg transition-opacity" data-id="element-21">
                  Settings
                </div>}
            </button>
            <button onClick={() => setIsNavExpanded(!isNavExpanded)} className="flex items-center justify-center rounded-lg p-2.5 text-slate-500 transition-all hover:bg-slate-800 hover:text-slate-300 w-full" data-id="element-22">
              {isNavExpanded ? <ChevronLeft className="h-5 w-5" data-id="element-23" /> : <Menu className="h-5 w-5" data-id="element-24" />}
            </button>
          </div>
        </motion.div>

        {/* Main Content Area */}
        <div className="flex flex-1 flex-col min-w-0 overflow-hidden bg-slate-50/50" data-id="element-25">
          {/* Header */}
          <header className="flex h-14 items-center gap-4 border-b border-slate-200 bg-white/80 backdrop-blur-sm px-6 shrink-0 z-10" data-id="element-26">
            <div className="w-full flex-1" data-id="element-27">
              <form data-id="element-28">
                <div className="relative max-w-md" data-id="element-29">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" data-id="element-30" />
                  <Input type="search" placeholder="Search users, loans, or sources..." className="w-full appearance-none bg-slate-100/50 border-transparent focus:bg-white pl-9 shadow-none h-9 text-sm transition-colors" data-id="element-31" />
                </div>
              </form>
            </div>
            <div className="flex items-center gap-2" data-id="element-32">
              <Button variant="ghost" size="icon" className="rounded-full text-slate-500 hover:bg-slate-100" data-id="element-33">
                <Bell className="h-5 w-5" data-id="element-34" />
              </Button>
              <Button variant="ghost" size="icon" className="rounded-full text-slate-500 hover:bg-slate-100" data-id="element-35">
                <UserCircle className="h-6 w-6" data-id="element-36" />
              </Button>
            </div>
          </header>

          {/* Main Scrollable Area with View Transitions */}
          <main className="flex-1 overflow-y-auto p-6 lg:p-8" data-id="element-37">
            <AnimatePresence mode="wait" data-id="element-38">
              <motion.div key={currentView} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="mx-auto max-w-[1600px] h-full" data-id="element-39">
                {currentView === 'overview' && <Overview onSelectUser={navigateToUser} data-id="element-40" />}
                {currentView === 'rate-tables' && <RateTables focusUserId={focusUserId} onConsumeFocus={() => setFocusUserId(null)} data-id="element-41" />}
                {currentView === 'cds-tiers' && <CDSTiers focusUserId={focusUserId} onConsumeFocus={() => setFocusUserId(null)} data-id="element-42" />}
                {currentView === 'draws' && <Draws data-id="element-43" />}
                {currentView === 'fees' && <FeesRecoupments data-id="element-44" />}
                {currentView === 'lo-view' && <LoanOfficerView data-id="element-45" />}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
    </BrowserRouter>;
}